import {
  Box,
  Skeleton,
  Stack,
  StackProps,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
} from '@mui/material';
import { CSSProperties, ElementType, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { aspectRatio } from 'utils/styles.utils';

import { useLocale } from 'hooks/useLocale';
import { EventCardFragment } from 'types/api/dato-operations.types';

import DatoImage from 'components/@common/DatoImage';
import LinkUnstyled from 'components/@common/LinkUnstyled';
import { formatEventDate } from 'components/@events/utils';
import TicketQuantityIcon from 'components/@tickets/TicketQuantityIcon';

const COVER_SIZE = { xs: 80, md: 120 };

const EventListItem = <T extends ElementType = typeof LinkUnstyled | 'div'>({
  children,
  component = 'div',
  ...rest
}: StackProps<T>) => (
  <Stack
    flex={1}
    sx={[
      (theme) => ({
        transiton: theme.transitions.create(['opacity']),
        '&:hover': {
          opacity: 0.9,
        },
        '&:active': {
          opacity: 0.8,
        },
      }),
    ]}
    direction="row"
    alignItems="center"
    component={component}
    {...rest}
  >
    {children}
  </Stack>
);

interface CoverProps {
  cover: EventCardFragment['cover'];
  style?: CSSProperties;
  priority?: boolean;
}

const Cover = ({ cover, style, priority = false }: CoverProps) => (
  <Box
    sx={{ ...aspectRatio(1, 1) }}
    position="relative"
    overflow="hidden"
    borderRadius={4}
    minHeight={COVER_SIZE}
    minWidth={COVER_SIZE}
    pr={2}
  >
    <DatoImage
      data={cover}
      priority={priority}
      fill
      style={{ objectFit: 'cover', ...style }}
      sizes="256px"
    />
  </Box>
);

interface ContentProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
}

const Content = ({ children, sx }: ContentProps) => (
  <Box sx={[{ ml: 2 }, ...(Array.isArray(sx) ? sx : [sx])]}>{children}</Box>
);

interface TitleProps {
  children: ReactNode;
  color?: TypographyProps['color'];
}

const Title = ({ children, color = 'grey.200' }: TitleProps) => (
  <Typography variant="h6" component="h2" pb={0.5} color={color}>
    {children}
  </Typography>
);

interface DateProps {
  startDatetime: EventCardFragment['startDatetime'];
  endDatetime: EventCardFragment['endDatetime'];
  timezone: EventCardFragment['timezone'];
  color?: TypographyProps['color'];
}

const Date = ({
  startDatetime,
  endDatetime,
  timezone,
  color = 'global01.400',
}: DateProps) => {
  const { locale } = useLocale();
  const formattedDate = formatEventDate({
    locale,
    startDate: startDatetime,
    timeZone: timezone,
    endDate: endDatetime,
  });

  return (
    <Typography
      variant="extraSmall"
      sx={{
        color: color,
      }}
    >
      {formattedDate}
    </Typography>
  );
};

interface CityProps {
  city: string;
  color?: TypographyProps['color'];
}

const City = ({ city, color = 'text.secondary' }: CityProps) => (
  <Typography variant="extraSmall" color={color}>
    <FormattedMessage id="event.city.in" values={{ city }} />
  </Typography>
);

interface TicketsIndicatorProp {
  quantity: number;
  color?: TypographyProps['color'];
}

const TicketsIndicator = ({ quantity, color }: TicketsIndicatorProp) => (
  <Box my={-0.5}>
    <TicketQuantityIcon quantity={quantity} color={color} />
  </Box>
);

const Loading = () => (
  <Box display="flex" flexGrow={1}>
    <Skeleton
      variant="rounded"
      sx={{
        minHeight: COVER_SIZE,
        minWidth: COVER_SIZE,
        mr: 2,
      }}
    />
    <Stack justifyContent="center">
      <Title>
        <Skeleton variant="rectangular" width={168} />
      </Title>
      <Typography variant="extraSmall" mb={0.5}>
        <Skeleton variant="rectangular" width={96} />
      </Typography>
      <Typography variant="extraSmall">
        <Skeleton variant="rectangular" width={63} />
      </Typography>
    </Stack>
  </Box>
);

EventListItem.Cover = Cover;
EventListItem.Content = Content;
EventListItem.Title = Title;
EventListItem.Date = Date;
EventListItem.City = City;
EventListItem.TicketsIndicator = TicketsIndicator;
EventListItem.Loading = Loading;

export default EventListItem;
